import { DunningActionModalComponent } from 'apps/middle/src/app/dunnings-module/components/dunning-action-modal/dunning-action-modal.component';
import { map, catchError, switchMap, withLatestFrom, delay, filter, takeUntil } from 'rxjs/operators';
import * as Routes from 'apps/middle/src/app/routes-definitions';
import { AppConstants } from 'apps/middle/src/app/app.constants';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { ActionType, NgbOffcanvas } from '@aston/foundation';
import { toObservable } from '@angular/core/rxjs-interop';
import { Injectable, inject } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { EMPTY, Observable } from 'rxjs';
import { Router } from '@angular/router';

import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import * as AppStoreActions from '../../app-store/actions';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';
import { ISuperDebtorIdentity } from '../../../debtors-module/models';


@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStoreModalEffects {

	private offcanvasService = inject(NgbOffcanvas);
	private router = inject(Router);
	private actions$ = inject(Actions);
	private store = inject(Store);

	payingCenterModeEnabled$ = toObservable<boolean>(inject(SuperDebtorPageStore).payingCenterModeEnabled)
	superDebtorIdentity$ = toObservable<ISuperDebtorIdentity>(inject(SuperDebtorPageStore).identity)

	onFormOpen$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.OpenForm, featureActions.OpenDuplicationForm, featureActions.ReOpenForm),
		map((action => {
			const modalRef = this.offcanvasService.open(DunningActionModalComponent, {
				...AppConstants.DEFAULT_OFFCANVAS_OPTIONS,
				backdropClass: 'offcanvas-backdrop-zindex',
				panelClass: 'offcanvas-panel-zindex offcanvas-panel-xxlarge p-0'
			});

			this.router.events.pipe(takeUntil(modalRef.closed)).subscribe(() => this.offcanvasService.dismiss());

			const component = (modalRef.componentInstance as DunningActionModalComponent);
			component.selectedPanel = action.panel;
			component.readOnly = action['readOnly'];
			component.resultEvent.pipe(
				catchError(reason => {
					this.store.dispatch(AppStoreActions.CloseConfirmationModal({actionType: reason}));
					return EMPTY;
				}),
				map(event => {
					const reason = event.type;

					if (reason === ActionType.CLOSE) {
						this.store.dispatch(featureActions.CloseForm());
					}
					if (reason === ActionType.CANCEL) {
						this.store.dispatch(featureActions.CloseForm());
						this.store.dispatch(featureActions.CancelForm(event.value.formIsDirty));
					}
					if (reason === ActionType.SUBMIT) {
						this.store.dispatch(featureActions.SubmitForm({param: event.value as boolean}));
					}
				})
			).subscribe();

			if (action.type === featureActions.ReOpenForm.type) {
				component.setAsDirty();
			}

			this.store.dispatch(featureActions.SetModalRef({param: modalRef}));
			this.store.dispatch(AppStoreActions.ToastReset());

			return AppStoreActions.Noop();
		}))
	), {dispatch: false});

	closeForm$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.CloseForm),
		withLatestFrom(
			this.store.select(featureSelectors.selectModalRef),
			this.payingCenterModeEnabled$,
			this.superDebtorIdentity$),
		map(([, modalRef, payingCenterMode, superDebtorIdentity]) => {
			modalRef?.dismiss();
			// Bug 38593: Les boutons Submit et Cancel DA ne doivent pas renvoyer vers la timeline si ouverts depuis le Suivi Récurrent
			if (!this.router.url.match(Routes.SuperDebtorDetailRecurringEventRoutePath)
				&& !this.router.url.match(Routes.SuperDebtorDetailAutomatedDeliveryRoutePath)
			) setTimeout(() => this.router.navigate([
				payingCenterMode ?
					Routes.getSuperDebtorPayingCenterDetailFullPath(superDebtorIdentity.id, Routes.SuperDebtorDetailDunningRoutePath) :
					Routes.getSuperDebtorDetailFullPath(superDebtorIdentity.id, Routes.SuperDebtorDetailDunningRoutePath),
			]), 150);
			return featureActions.SetModalRef(null);
		})
	));


	initSuccessEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.InitSuccess),
		withLatestFrom(this.store.select(featureSelectors.selectModalRef)),
		// This should happen just after onFormOpen$
		// guarded for some cases when the success action happens after the modal is closed
		delay(100), filter(([_, modalRef]) => !!modalRef?.componentInstance),
		map(([_, modalRef]) => {
			(modalRef.componentInstance as DunningActionModalComponent).setPanel();
			return AppStoreActions.Noop();
		})
	));

	switchToEdit$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.SwitchToEdit),
		withLatestFrom(this.superDebtorIdentity$),
		switchMap(([action, superDebtorIdentity]) => [
			featureActions.CancelCompletionForm(),
			featureActions.OpenForm(superDebtorIdentity.id,
				{ id: action.param.id })
		])
	))
}
